import { useState, type FormEvent } from 'react';

import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';

// CB-REQ VEN-03 at v1.986 (venue slice 3B-3): where a venue's own item is installed. A unit names its
// room; a counted item splits by room and the rest is loose. The database refuses a room of another
// venue and more by room than is on hand; this offers only the owner's rooms.

export function InstalledSection({
  tracking,
  units,
  onHand,
  counts,
  rooms,
  mayManage,
  onSetUnit,
  onSetCounts,
}: {
  tracking: 'serialised' | 'bulk' | 'service';
  units: ReadonlyArray<{ id: string; label: string; installedRoomId: string | null }>;
  onHand: number;
  counts: ReadonlyArray<{ roomId: string; quantity: number }>;
  /** The owning venue's rooms. */
  rooms: ReadonlyArray<{ id: string; name: string }>;
  mayManage: boolean;
  onSetUnit: (assetId: string, roomId: string | null) => Promise<unknown>;
  onSetCounts: (counts: Array<{ roomId: string; quantity: number }>) => Promise<unknown>;
}) {
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      rooms.map((r) => [r.id, String(counts.find((c) => c.roomId === r.id)?.quantity ?? '')]),
    ),
  );
  const placed = counts.reduce((sum, c) => sum + c.quantity, 0);
  const roomName = (id: string | null) => rooms.find((r) => r.id === id)?.name ?? 'Not installed';

  function save(event: FormEvent) {
    event.preventDefault();
    void onSetCounts(
      rooms.flatMap((r) => {
        const n = Number(draft[r.id] || 0);
        return n > 0 ? [{ roomId: r.id, quantity: n }] : [];
      }),
    );
  }

  return (
    <section className="flex flex-col gap-2" aria-labelledby="installed">
      <h2 id="installed" className="section-title">
        Installed
      </h2>
      {tracking === 'serialised' ? (
        <ul className="flex flex-col gap-2">
          {units.map((u) => (
            <li key={u.id} className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-ink">{u.label}</span>
              {mayManage ? (
                <label className="flex items-center gap-2">
                  <span className="sr-only">{`Where ${u.label} is installed`}</span>
                  <select
                    className="input py-1"
                    aria-label={`Where ${u.label} is installed`}
                    value={u.installedRoomId ?? ''}
                    onChange={(e) => void onSetUnit(u.id, e.currentTarget.value || null)}
                  >
                    <option value="">Not installed</option>
                    {rooms.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <span className="text-ink-soft">{roomName(u.installedRoomId)}</span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <form onSubmit={save} className="flex flex-col gap-2">
          <p className="text-sm text-ink-soft">{`${Math.max(onHand - placed, 0)} loose`}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {rooms.map((r) => (
              <Field
                key={r.id}
                label={`In ${r.name}`}
                type="number"
                min={0}
                disabled={!mayManage}
                value={draft[r.id] ?? ''}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setDraft((d) => ({ ...d, [r.id]: value }));
                }}
              />
            ))}
          </div>
          {mayManage ? (
            <div>
              <Button type="submit">Save</Button>
            </div>
          ) : null}
        </form>
      )}
    </section>
  );
}
